import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Styled from './BlockedProductModal.styles';
import ModalWindow from '../ModalWindow';
import Button from '../Button';
import { getFromLocalStorage } from '../../global/helpers';
import { selectors as productsSelectors } from '../../containers/ProductsPage/reducer';
import { selectors as networkSelectors } from '../../containers/OrdersPage/reducer';
import { SourceData } from '../../containers/OrdersPage/types';
import * as actions from '../../containers/OrdersPage/actions';
import { baseTheme } from '../../global/styles/theme';

interface BlockedProductModalProps {
  setReason?: (reason: string) => void;
  reason?: string | null;
  setSelectedSource?: (source: SourceData | null) => void;
  selectedSource?: SourceData | null;
  onClose: () => void;
  onBlock: () => void;
  mapping: string;
}

const BlockedProductModal = ({
  setReason,
  reason,
  setSelectedSource,
  selectedSource,
  onClose,
  onBlock,
  mapping,
}: BlockedProductModalProps) => {
  const { t } = useTranslation();
  const networkForContent = useSelector(productsSelectors.network);
  const sourcesByMappingCodeData = useSelector(
    networkSelectors.sourcesByMappingCodeData
  );
  const storageNetworkForContent = getFromLocalStorage('networkData');
  const dispatch = useDispatch();

  // Локальное состояние для reason и ошибки валидации
  const [reasonError, setReasonError] = useState<string>('');

  const networkCode =
    mapping ??
    (networkForContent?.code
      ? networkForContent?.code
      : storageNetworkForContent?.code);

  const handleCancelClick = () => {
    setReason && setReason('');
    setReasonError('');
    onClose();
  };

  // Валидация формы: если источник выбран, reason обязателен
  const validateForm = (): boolean => {
    if (selectedSource && !reason?.trim()) {
      setReasonError('Укажите причину блокировки');
      return false;
    }
    setReasonError('');
    return true;
  };

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }

    // Валидация: если источник выбран, reason обязателен
    if (!validateForm()) {
      return;
    }

    onBlock();
    onClose();
  };

  const handleSubmitClick = () => {
    handleSubmit();
  };

  const handleSourceChange = (
    _event: React.SyntheticEvent,
    newValue: SourceData | null
  ) => {
    setSelectedSource && setSelectedSource(newValue as SourceData);
    // Если источник выбран, но reason пустой - показываем ошибку
    if (newValue && !reason?.trim()) {
      setReasonError('Укажите причину блокировки');
    } else {
      setReasonError('');
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setReason && setReason(value);
    // Если reason заполнен, убираем ошибку
    if (value.trim()) {
      setReasonError('');
    } else if (selectedSource) {
      // Если источник выбран, но reason пустой - показываем ошибку
      setReasonError('Укажите причину блокировки');
    }
  };

  useEffect(() => {
    dispatch(
      actions.getSourcesByMappingCode.request({
        mappingCode: networkCode || '',
      })
    );
  }, [dispatch, networkCode]);

  return (
    <ModalWindow onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Styled.BlockedProductModalContainer>
          <div className="products-title">
            {t('EditPage.BLOCKED_MODAL.TITLE')}
          </div>
          <div className="products-sub-title">
            {t('EditPage.BLOCKED_MODAL.SUB_TITLE')}
          </div>

          <div className="products-sub-title">В сети: {networkCode}</div>

          {/* Выбор источника - не обязателен */}
          <Autocomplete
            options={sourcesByMappingCodeData || []}
            getOptionLabel={(option) => option.name || ''}
            value={selectedSource}
            onChange={handleSourceChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Выберите аптеку (необязательно)"
              />
            )}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '10px 20px',
                borderRadius: '5px',
                border: `1px solid ${baseTheme.colors.mainLightBlue}`,
                '& fieldset': {
                  border: 'none',
                },
                '&:hover fieldset': {
                  border: 'none',
                },
                '&.Mui-focused fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                color: baseTheme.colors.mainLightBlue,
                fontSize: '16px',
                fontWeight: 700,
                cursor: 'pointer',
              },
              '& .MuiAutocomplete-endAdornment': {
                color: baseTheme.colors.mainLightBlue,
              },
            }}
          />

          {/* Поле reason - обязательно, если выбран источник */}
          <div className="reason-input">
            <input
              type="text"
              placeholder={
                selectedSource
                  ? 'Укажите причину (обязательно)'
                  : 'Укажите причину'
              }
              value={reason || undefined}
              onChange={handleReasonChange}
              style={{
                padding: '8px',
                width: '100%',
                borderRadius: '6px',
                border: reasonError
                  ? `1px solid ${baseTheme.colors.error}`
                  : '1px solid #ccc',
              }}
            />
            {reasonError && (
              <div
                style={{
                  color: baseTheme.colors.error,
                  fontSize: '12px',
                  marginTop: '4px',
                }}
              >
                {reasonError}
              </div>
            )}
          </div>

          <Styled.ModalButtonsContainer>
            <div className="save-btn">
              <Button
                onClick={handleSubmitClick}
                text={t('EditPage.BLOCKED_MODAL.BLOCKED_BTN')}
                variant="contained"
              />
            </div>
            <div className="cancel-btn">
              <Button
                onClick={handleCancelClick}
                text={t('EditPage.BLOCKED_MODAL.CANCEL_BTN')}
                variant="outlined"
              />
            </div>
          </Styled.ModalButtonsContainer>
        </Styled.BlockedProductModalContainer>
      </form>
    </ModalWindow>
  );
};

export default BlockedProductModal;
